import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from '@tanstack/react-router';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Check, CircleAlert, ExternalLink, GripVertical, LoaderCircle, Plus, Rocket } from 'lucide-react';
import {
  diffContent,
  liveContentVersion,
  useCaseStudies,
  useDraft,
  useLabItems,
  useLatestPublish,
  usePublish,
  useReorder,
  useSetLinkLive,
  useSetVisible,
  type CaseStudyRow,
  type LabRow,
} from '@/lib/work';
import type { SiteContent } from '@site/shared/content';
import { ago, when } from '@/lib/format';
import { Page, PageHeader } from '@/components/Shell';
import { Switch } from '@/components/form';
import { Empty, ListSkeleton, LoadError, SectionLabel } from '@/components/ui';

/**
 * The site's case studies and lab, in the order the site shows them. Every
 * change here edits the working copy; nothing reaches bitbew.com until
 * Publish. The publish panel lists in plain words what will change.
 */
export function WorkScreen() {
  const studies = useCaseStudies();
  const lab = useLabItems();
  const draft = useDraft();
  const latest = useLatestPublish();
  const [publishing, setPublishing] = useState(false);

  const changes = useMemo(
    () => diffContent((latest.data?.content as unknown as SiteContent) ?? null, draft.data ?? null),
    [latest.data, draft.data],
  );

  return (
    <>
      <PageHeader title="Work">
        <button
          type="button"
          className="btn btn-primary"
          disabled={!changes.length || publishing}
          onClick={() => setPublishing(true)}
        >
          <Rocket className="size-4" aria-hidden />
          <span className="max-sm:sr-only">Publish</span>
          {changes.length > 0 && <span className="count !bg-ink !text-paper">{changes.length}</span>}
        </button>
      </PageHeader>
      <Page>
        <div className="grid grid-cols-1 gap-10">
          <PublishState
            changes={changes}
            latest={latest.data ?? null}
            open={publishing}
            onClose={() => setPublishing(false)}
          />

          <section aria-labelledby="w-studies" className="min-w-0">
            <SectionLabel
              aside={
                <Link to="/work/$caseStudyId" params={{ caseStudyId: 'new' }} className="btn btn-quiet btn-sm">
                  <Plus className="size-4" aria-hidden /> New case study
                </Link>
              }
            >
              <span id="w-studies">Case studies</span>
            </SectionLabel>
            <p className="mb-3 text-sm text-fg-dim">
              Drag to set the order the site shows them in. Hidden case studies keep their record, and their address
              forwards to /work.
            </p>
            {studies.isPending ? (
              <ListSkeleton rows={4} />
            ) : studies.error ? (
              <LoadError error={studies.error} retry={() => studies.refetch()} />
            ) : studies.data.length === 0 ? (
              <Empty title="No case studies">Add the first one, then publish it.</Empty>
            ) : (
              <SortableRows table="case_studies" rows={studies.data} render={(row) => <StudyRow row={row as CaseStudyRow} />} />
            )}
          </section>

          <section aria-labelledby="w-lab" className="min-w-0">
            <SectionLabel
              aside={
                <Link to="/work/lab/$labId" params={{ labId: 'new' }} className="btn btn-quiet btn-sm">
                  <Plus className="size-4" aria-hidden /> New lab item
                </Link>
              }
            >
              <span id="w-lab">The Lab</span>
            </SectionLabel>
            <p className="mb-3 text-sm text-fg-dim">
              Prototypes built for ourselves. Their status lines stay honest, and they never read as client work.
            </p>
            {lab.isPending ? (
              <ListSkeleton rows={2} />
            ) : lab.error ? (
              <LoadError error={lab.error} retry={() => lab.refetch()} />
            ) : (
              <SortableRows table="lab_items" rows={lab.data} render={(row) => <LabRowView row={row as LabRow} />} />
            )}
          </section>
        </div>
      </Page>
    </>
  );
}

/* ------------------------------------------------------------- sortable list */

function SortableRows({
  table,
  rows,
  render,
}: {
  table: 'case_studies' | 'lab_items';
  rows: { id: string }[];
  render: (row: { id: string }) => React.ReactNode;
}) {
  const reorder = useReorder(table);
  const [order, setOrder] = useState(rows.map((r) => r.id));
  useEffect(() => setOrder(rows.map((r) => r.id)), [rows]);
  const byId = new Map(rows.map((r) => [r.id, r]));

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 180, tolerance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  function onDragEnd(e: DragEndEvent) {
    if (!e.over || e.active.id === e.over.id) return;
    const next = arrayMove(order, order.indexOf(String(e.active.id)), order.indexOf(String(e.over.id)));
    setOrder(next);
    reorder.mutate(next);
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
      <SortableContext items={order} strategy={verticalListSortingStrategy}>
        <ul className="divide-y divide-edge rounded-lg border border-edge bg-bg-raised">
          {order.map((id) => (byId.has(id) ? <SortableItem key={id} id={id}>{render(byId.get(id)!)}</SortableItem> : null))}
        </ul>
      </SortableContext>
      {reorder.error && <p className="error-text mt-2">The new order was not saved: {reorder.error.message}</p>}
    </DndContext>
  );
}

function SortableItem({ id, children }: { id: string; children: React.ReactNode }) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });
  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={`relative flex items-center gap-2 bg-bg-raised pr-3 pl-1 first:rounded-t-lg last:rounded-b-lg ${
        isDragging ? 'z-10 shadow-lg ring-1 ring-edge-strong' : ''
      }`}
    >
      <button
        type="button"
        ref={setActivatorNodeRef}
        {...attributes}
        {...listeners}
        className="btn btn-ghost btn-icon shrink-0 cursor-grab touch-none active:cursor-grabbing"
        aria-label="Drag to reorder (or press space, then the arrow keys)"
      >
        <GripVertical className="size-4" aria-hidden />
      </button>
      {children}
    </li>
  );
}

/* --------------------------------------------------------------------- rows */

function StudyRow({ row }: { row: CaseStudyRow }) {
  const setVisible = useSetVisible('case_studies');
  const setLive = useSetLinkLive();
  const data = row.data as { name?: string; industry?: string; url?: string; domain?: string; live?: boolean };
  const neverPublished = !row.first_published_at;
  const linkLive = data.live !== false;

  return (
    <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-4 gap-y-2 py-3">
      <Link to="/work/$caseStudyId" params={{ caseStudyId: row.id }} className="min-w-0 flex-1 basis-56">
        <p className="truncate font-semibold hover:underline">{data.name ?? row.id}</p>
        <p className="truncate text-sm text-fg-dim">
          <span className="num text-xs">/work/{row.slug}</span>
          {data.industry && <span> · {data.industry}</span>}
        </p>
      </Link>
      <div className="flex items-center gap-2">
        {neverPublished && <span className="chip chip-warn">Never published</span>}
        {!row.visible && !neverPublished && <span className="chip">Hidden</span>}
        {data.url && !linkLive && <span className="chip">Links off</span>}
      </div>
      <div className="flex items-center gap-4">
        {data.url && (
          <label className="flex items-center gap-2 text-sm text-fg-dim">
            <Switch
              on={linkLive}
              label={`Link out to ${data.domain ?? data.url}`}
              disabled={setLive.isPending}
              onChange={(live) => setLive.mutate({ row, live })}
            />
            <span>Link</span>
          </label>
        )}
        <label className="flex items-center gap-2 text-sm text-fg-dim">
          <Switch
            on={row.visible}
            label={`Show ${data.name ?? row.id} on the site`}
            disabled={setVisible.isPending}
            onChange={(visible) => setVisible.mutate({ id: row.id, visible })}
          />
          <span>Shown</span>
        </label>
      </div>
    </div>
  );
}

function LabRowView({ row }: { row: LabRow }) {
  const setVisible = useSetVisible('lab_items');
  const data = row.data as { name?: string; status?: string };
  return (
    <div className="flex min-w-0 flex-1 items-center gap-4 py-3">
      <Link to="/work/lab/$labId" params={{ labId: row.id }} className="min-w-0 flex-1">
        <p className="truncate font-semibold hover:underline">{data.name ?? row.id}</p>
        <p className="truncate text-sm text-fg-dim">{data.status}</p>
      </Link>
      <label className="flex items-center gap-2 text-sm text-fg-dim">
        <Switch
          on={row.visible}
          label={`Show ${data.name ?? row.id} on the site`}
          disabled={setVisible.isPending}
          onChange={(visible) => setVisible.mutate({ id: row.id, visible })}
        />
        <span>Shown</span>
      </label>
    </div>
  );
}

/* ------------------------------------------------------------------ publish */

type Stage =
  | { step: 'review' }
  | { step: 'publishing' }
  | { step: 'waiting'; id: number; deploy: string; detail: string; since: number }
  | { step: 'live'; id: number }
  | { step: 'saved'; id: number; detail: string }
  | { step: 'error'; message: string };

const WAIT_LIMIT_MS = 6 * 60_000;

function PublishState({
  changes,
  latest,
  open,
  onClose,
}: {
  changes: ReturnType<typeof diffContent>;
  latest: { id: number; created_at: string; summary: string | null; deploy_status: string } | null;
  open: boolean;
  onClose: () => void;
}) {
  const publish = usePublish();
  const [note, setNote] = useState('');
  const [stage, setStage] = useState<Stage>({ step: 'review' });
  const timer = useRef<number | null>(null);

  useEffect(() => () => (timer.current ? window.clearInterval(timer.current) : undefined), []);
  useEffect(() => {
    if (open) setStage({ step: 'review' });
  }, [open]);

  function watch(id: number, since: number) {
    timer.current = window.setInterval(async () => {
      const live = await liveContentVersion();
      if (live !== null && live >= id) {
        window.clearInterval(timer.current!);
        setStage({ step: 'live', id });
      } else if (Date.now() - since > WAIT_LIMIT_MS) {
        window.clearInterval(timer.current!);
        setStage({
          step: 'saved',
          id,
          detail:
            'Cloudflare took the request, but bitbew.com has not switched over yet. It usually does within a few minutes; check the build in the Cloudflare dashboard if it does not.',
        });
      }
    }, 10_000);
  }

  async function go() {
    setStage({ step: 'publishing' });
    try {
      const r = await publish.mutateAsync(note);
      setNote('');
      if (r.deploy === 'triggered') {
        const since = Date.now();
        setStage({ step: 'waiting', id: r.id, deploy: r.deploy, detail: r.detail, since });
        watch(r.id, since);
      } else {
        setStage({ step: 'saved', id: r.id, detail: r.detail });
      }
    } catch (err) {
      setStage({ step: 'error', message: err instanceof Error ? err.message : String(err) });
    }
  }

  if (!open) {
    return latest ? (
      <p className="flex flex-wrap items-center gap-x-2 text-sm text-fg-dim">
        <Check className="size-4 text-ok" aria-hidden />
        bitbew.com shows publish <span className="num text-xs">#{latest.id}</span>
        <span>· {when(latest.created_at)}</span>
        {latest.summary && <span>· {latest.summary}</span>}
        {changes.length > 0 && (
          <span className="font-semibold text-fg">
            · {changes.length} unpublished {changes.length === 1 ? 'change' : 'changes'}
          </span>
        )}
      </p>
    ) : null;
  }

  return (
    <section aria-label="Publish" className="rounded-lg border border-edge-strong bg-bg-raised p-4 sm:p-5">
      {stage.step === 'review' && (
        <div className="grid gap-4">
          <div>
            <p className="font-semibold">Publish to bitbew.com</p>
            <p className="text-sm text-fg-dim">The site is rebuilt with these changes. It takes about a minute.</p>
          </div>
          <ul className="grid gap-1.5 text-sm">
            {changes.map((c, i) => (
              <li key={i} className="flex gap-2">
                <span className="mt-1.5 size-1.5 shrink-0 bg-ink" aria-hidden />
                {c.what}
              </li>
            ))}
          </ul>
          <div className="field">
            <label className="label" htmlFor="pub-note">
              Note for the history <span className="font-normal text-fg-dim">(optional)</span>
            </label>
            <input
              id="pub-note"
              className="input"
              placeholder="e.g. Added Ekaksh, hid Social Surfers"
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </div>
          <div className="flex gap-2">
            <button type="button" className="btn btn-primary" onClick={go}>
              <Rocket className="size-4" aria-hidden /> Publish now
            </button>
            <button type="button" className="btn btn-ghost" onClick={onClose}>
              Not yet
            </button>
          </div>
        </div>
      )}

      {stage.step === 'publishing' && (
        <Progress
          steps={[
            ['Saving the snapshot', 'active'],
            ['Asking Cloudflare to rebuild bitbew.com', 'todo'],
            ['Waiting for the site to switch over', 'todo'],
          ]}
        />
      )}

      {stage.step === 'waiting' && (
        <Progress
          steps={[
            [`Saved as publish #${stage.id}`, 'done'],
            [stage.detail, 'done'],
            [`Waiting for bitbew.com to switch over (started ${ago(new Date(stage.since).toISOString())} ago)`, 'active'],
          ]}
        />
      )}

      {stage.step === 'live' && (
        <div className="flex flex-wrap items-center gap-3">
          <Check className="size-5 text-ok" aria-hidden />
          <p className="font-semibold">Live. bitbew.com now shows publish #{stage.id}.</p>
          <a href="https://bitbew.com/work" target="_blank" rel="noreferrer" className="btn btn-quiet btn-sm">
            <ExternalLink className="size-4" aria-hidden /> Open /work
          </a>
          <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>
            Done
          </button>
        </div>
      )}

      {stage.step === 'saved' && (
        <div className="grid gap-3">
          <p className="flex items-center gap-2 font-semibold">
            <Check className="size-5 text-ok" aria-hidden /> Saved as publish #{stage.id}.
          </p>
          <p className="text-sm text-fg-dim">{stage.detail}</p>
          <div>
            <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>
              Done
            </button>
          </div>
        </div>
      )}

      {stage.step === 'error' && (
        <div className="grid gap-3">
          <p className="flex items-center gap-2 font-semibold text-alert">
            <CircleAlert className="size-5" aria-hidden /> Nothing was published.
          </p>
          <p className="text-sm text-fg-dim">{stage.message}</p>
          <div className="flex gap-2">
            <button type="button" className="btn btn-quiet btn-sm" onClick={() => setStage({ step: 'review' })}>
              Try again
            </button>
            <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

function Progress({ steps }: { steps: [string, 'done' | 'active' | 'todo'][] }) {
  return (
    <ol className="grid gap-2.5 text-sm" aria-live="polite">
      {steps.map(([label, state], i) => (
        <li key={i} className={`flex items-start gap-2.5 ${state === 'todo' ? 'text-fg-faint' : ''}`}>
          {state === 'done' ? (
            <Check className="mt-0.5 size-4 shrink-0 text-ok" aria-hidden />
          ) : state === 'active' ? (
            <LoaderCircle className="mt-0.5 size-4 shrink-0 animate-spin motion-reduce:animate-none" aria-hidden />
          ) : (
            <span className="mt-1.5 ml-1 size-1.5 shrink-0 rounded-full bg-edge-strong" aria-hidden />
          )}
          <span className={state === 'active' ? 'font-semibold' : ''}>{label}</span>
        </li>
      ))}
    </ol>
  );
}
